import { MIN_POOL_SIZE, type Decade, type Genre, type Track } from "../../shared/domain.ts";

/**
 * Pools are what a Slot or a Filter draws from.
 *
 * The floor is the whole point: a combination with too few Tracks is never
 * offered, so a player cannot reach a mode that repeats itself or comes up
 * empty. That failure — modes that exist but don't work — is the thing this
 * game was built in reaction to.
 */

/**
 * An obscurity band, cut from `percentile` rather than raw `recognisability`.
 *
 * This started as two absolute recognisability thresholds, and they broke twice
 * for the same reason. Measured the first time the scoring model changed: the
 * Catalog's median recognisability moved 0.318 → 0.452, which left a 0.4
 * threshold matching 2163 of 2947 Tracks. "Chart Toppers" had quietly become
 * three-quarters of the game, and the Pool floor could not catch it because a
 * band that is far too *large* still clears a minimum.
 *
 * An absolute cut on a score whose distribution moves every rebuild is a
 * standing trap. `percentile` is defined as the Track's standing within its own
 * decade, so a cut on it means the same thing at any Catalog size and after any
 * rescore — "the top quarter of its era" needs no recalibration, and a bigger
 * harvest makes the bands deeper instead of breaking them.
 */
export const CHART_TOPPER_MIN = 0.75;
export const DEEP_CUT_MAX = 0.26;

export type Band = "chart" | "deep";

export interface Filter {
  /** Empty means any genre. Several are a union — rock *or* metal. */
  genres: Genre[];
  /** Empty means any era. Several are a union. */
  decades: Decade[];
  band: Band | null;
}

/** The unconstrained Filter — the whole Catalog. */
export const ANY_FILTER: Filter = { genres: [], decades: [], band: null };

export function inBand(track: Track, band: Band): boolean {
  return band === "chart"
    ? track.percentile >= CHART_TOPPER_MIN
    : track.percentile <= DEEP_CUT_MAX;
}

/** Union within an axis, intersection across them. */
export function tracksMatching(tracks: Track[], filter: Filter): Track[] {
  return tracks.filter((t) => {
    if (filter.decades.length > 0 && !filter.decades.includes(t.decade)) return false;
    if (filter.genres.length > 0 && !filter.genres.some((g) => t.genres.includes(g))) return false;
    if (filter.band && !inBand(t, filter.band)) return false;
    return true;
  });
}

export function isOffered(tracks: Track[], filter: Filter, floor = MIN_POOL_SIZE): boolean {
  return tracksMatching(tracks, filter).length >= floor;
}

/**
 * A stable identity for a Filter, used to key seen-sets.
 *
 * Canonical on purpose: both axes are sorted, so picking rock then metal keys
 * the same Pool as metal then rock. Without that, reordering a selection would
 * hand the player a second, empty seen-set for Tracks they had already heard.
 */
export function filterKey(filter: Filter): string {
  const genres = [...filter.genres].sort().join(",") || "any";
  const decades = [...filter.decades].sort().join(",") || "any";
  return `g:${genres}|d:${decades}|b:${filter.band ?? "any"}`;
}

/**
 * Picks the next Unlimited Track, avoiding anything already seen in this Pool
 * until the Pool is exhausted — then starts over.
 *
 * Returns the reset flag so the caller can clear its seen-set; without that the
 * player would hit a permanent dead end instead of a fresh cycle.
 */
export function drawNext(
  pool: Track[],
  seenIds: ReadonlySet<number>,
  random: () => number = Math.random,
): { track: Track | null; exhausted: boolean } {
  if (pool.length === 0) return { track: null, exhausted: false };

  const unseen = pool.filter((t) => !seenIds.has(t.id));

  if (unseen.length === 0) {
    const track = pool[Math.floor(random() * pool.length)] ?? null;
    return { track, exhausted: true };
  }

  return { track: unseen[Math.floor(random() * unseen.length)] ?? null, exhausted: false };
}
